import React from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin, Clock, ArrowUpRight, Instagram, Facebook } from "lucide-react";
import { useI18n } from "../../i18n";
import { CONTACT, ABOUT_IMG } from "../../data";

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export function About() {
  const { t } = useI18n();
  return (
    <section id="about" className="relative bg-[#131311] py-28 md:py-40" data-testid="about">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="overflow-hidden rounded-2xl spotlight">
            <img src={ABOUT_IMG} alt="Anna al lavoro" className="w-full aspect-[4/5] object-cover" />
          </div>
          <span className="absolute -bottom-6 -right-2 md:right-6 font-script text-lime text-5xl md:text-6xl rotate-[-6deg]">Anna</span>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-[11px] uppercase tracking-[0.4em] text-lime mb-5">
            {t.about.kicker}
          </motion.p>
          <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display font-light text-[clamp(2.6rem,7vw,5rem)] leading-[0.95] tracking-[-0.02em] text-cream mb-8">
            {t.about.title}
          </motion.h2>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-cream/70 text-lg leading-relaxed max-w-[52ch] mb-5">
            {t.about.p1}
          </motion.p>
          <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-cream/60 leading-relaxed max-w-[52ch]">
            {t.about.p2}
          </motion.p>

          <div className="grid grid-cols-3 gap-4 mt-12 border-t border-white/10 pt-10">
            {t.about.stats.map((s, i) => (
              <motion.div key={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <div className="font-display text-3xl md:text-4xl text-lime mb-2">{s.k}</div>
                <div className="font-body text-[11px] uppercase tracking-[0.2em] text-cream/50 leading-snug">{s.v}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Contact() {
  const { t } = useI18n();
  return (
    <section id="contact" className="relative bg-[#0b0b0a] py-28 md:py-40" data-testid="contact">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-[11px] uppercase tracking-[0.4em] text-lime mb-5">
          {t.contact.kicker}
        </motion.p>
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display font-light text-[clamp(2.4rem,6.5vw,5rem)] leading-[0.98] tracking-[-0.02em] text-cream max-w-[16ch] mb-6">
          {t.contact.title}
        </motion.h2>
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-body text-cream/60 max-w-[48ch] text-lg mb-14">
          {t.contact.lead}
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-stretch">
          <div className="flex flex-col gap-8">
            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={CONTACT.phoneHref} data-testid="contact-call-btn" className="group flex-1 flex items-center justify-between gap-4 border border-white/15 rounded-2xl p-6 hover:border-lime transition-colors">
                <div>
                  <div className="font-body text-[11px] uppercase tracking-[0.25em] text-cream/50 mb-1.5">{t.contact.callLabel}</div>
                  <div className="font-display text-xl text-cream">{CONTACT.phoneDisplay}</div>
                </div>
                <span className="w-11 h-11 rounded-full bg-white/8 flex items-center justify-center group-hover:bg-lime group-hover:text-[#0b0b0a] text-cream transition-colors">
                  <Phone size={18} />
                </span>
              </a>
              <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="group flex-1 flex items-center justify-between gap-4 bg-lime text-[#0b0b0a] rounded-2xl p-6 hover:bg-[#b6ff3c] transition-colors">
                <div>
                  <div className="font-body text-[11px] uppercase tracking-[0.25em] text-[#0b0b0a]/60 mb-1.5">{t.contact.waLabel}</div>
                  <div className="font-display text-xl">{CONTACT.whatsappDisplay}</div>
                </div>
                <span className="w-11 h-11 rounded-full bg-[#0b0b0a]/10 flex items-center justify-center">
                  <MessageCircle size={18} />
                </span>
              </a>
            </div>

            {/* Address */}
            <div className="border border-white/12 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <MapPin size={16} className="text-lime" />
                <span className="font-body text-[11px] uppercase tracking-[0.25em] text-cream/50">{t.contact.addressLabel}</span>
              </div>
              <p className="font-display text-lg text-cream mb-4">{CONTACT.address}</p>
              <a href={CONTACT.mapLink} target="_blank" rel="noreferrer" data-testid="contact-map-link" className="inline-flex items-center gap-1.5 font-body text-sm text-lime link-underline">
                Google Maps <ArrowUpRight size={15} />
              </a>
            </div>

            {/* Hours */}
            <div className="border border-white/12 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock size={16} className="text-lime" />
                <span className="font-body text-[11px] uppercase tracking-[0.25em] text-cream/50">{t.contact.hoursLabel}</span>
              </div>
              <ul className="divide-y divide-white/8">
                {t.contact.hours.map((h, i) => (
                  <li key={i} className="flex items-center justify-between py-2.5">
                    <span className="font-body text-cream/80">{h.d}</span>
                    <span className="font-body text-cream/50 text-sm">{h.h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social */}
            <div className="flex items-center gap-3">
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" aria-label="Instagram" className="group flex items-center gap-3 flex-1 border border-white/12 rounded-2xl p-4 hover:border-lime transition-colors">
                <span className="w-10 h-10 rounded-full bg-white/8 flex items-center justify-center text-cream group-hover:bg-lime group-hover:text-[#0b0b0a] transition-colors"><Instagram size={18} /></span>
                <span className="font-body text-sm text-cream/80 group-hover:text-cream">@carpediem.villesse</span>
              </a>
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" data-testid="contact-facebook" aria-label="Facebook" className="group flex items-center justify-center w-[58px] h-[58px] shrink-0 border border-white/12 rounded-2xl text-cream hover:border-lime hover:text-lime transition-colors">
                <Facebook size={20} />
              </a>
            </div>
          </div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="min-h-[340px] rounded-2xl overflow-hidden border border-white/12"
          >
            <iframe
              title="map"
              src={CONTACT.mapEmbed}
              className="w-full h-full min-h-[340px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const { t } = useI18n();
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-[#131311] border-t border-white/10 py-16" data-testid="footer">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div>
            <span className="font-script text-lime text-5xl block -mb-1">Carpe Diem</span>
            <span className="font-body text-[10px] uppercase tracking-[0.4em] text-cream/50">Hair Style by Anna — Villesse (GO)</span>
            <div className="flex items-center gap-3 mt-5">
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-cream/80 hover:bg-lime hover:text-[#0b0b0a] hover:border-lime transition-colors">
                <Instagram size={17} />
              </a>
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-cream/80 hover:bg-lime hover:text-[#0b0b0a] hover:border-lime transition-colors">
                <Facebook size={17} />
              </a>
            </div>
          </div>
          <p className="font-display italic text-xl md:text-2xl text-cream/70 max-w-[24ch] md:text-right">
            “{t.footer.made}”
          </p>
        </div>
        <div className="mt-12 pt-6 border-t border-white/8 flex flex-col sm:flex-row justify-between gap-3 font-body text-xs text-cream/40">
          <span>© {year} Carpe Diem. {t.footer.rights}</span>
          <span>{CONTACT.address}</span>
        </div>
      </div>
    </footer>
  );
}
